<template>
  <div
    class="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50 flex flex-col"
  >
    <!-- Subtle background glow -->
    <div class="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div class="absolute -top-32 -left-24 h-72 w-72 rounded-full bg-sky-400/15 blur-3xl" />
      <div class="absolute -bottom-40 -right-24 h-80 w-80 rounded-full bg-cyan-500/15 blur-3xl" />
    </div>

    <!-- Top bar -->
    <header
      class="w-full border-b border-zinc-200/70 dark:border-zinc-800/70 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-sm"
    >
      <div class="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <div
            class="h-8 w-8 rounded-2xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center"
          >
            <span class="text-[18px] font-semibold text-sky-500">NG</span>
          </div>
          <div class="leading-tight">
            <p class="text-xs uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
              NexGridCo
            </p>
            <p class="text-sm font-medium text-zinc-900 dark:text-zinc-50">
              Workspace access
            </p>
          </div>
        </div>

        <div
          class="hidden sm:inline-flex items-center gap-2 rounded-full border border-zinc-200/80 dark:border-zinc-800/80 bg-zinc-100/70 dark:bg-zinc-900/70 px-3 py-1"
        >
          <span class="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          <span class="text-[11px] font-medium tracking-[0.18em] uppercase text-zinc-500 dark:text-zinc-400">
            Step 2 of 2 · Legal
          </span>
        </div>
      </div>
    </header>

    <!-- Content -->
    <main class="flex-1 flex items-stretch justify-center">
      <div class="w-full max-w-3xl mx-auto px-4 py-8 lg:py-10">
        <div class="mb-6 lg:mb-8 space-y-2">
          <p class="text-[11px] uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">
            Legal onboarding
          </p>
          <h1 class="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            Before you start using NexGridCo
          </h1>
          <p class="text-sm text-zinc-600 dark:text-zinc-400 max-w-xl">
            To continue, please confirm that you agree with our Terms of Use, Privacy Policy,
            and Data Protection rules for your account and saved projects.
          </p>
        </div>

        <section
          class="rounded-2xl border border-zinc-200/80 dark:border-zinc-800/80 bg-white/90 dark:bg-[#05060a]/95 shadow-2xl shadow-zinc-900/5 p-4 sm:p-5 space-y-5"
        >
          <!-- Terms text -->
          <div
            class="space-y-2 text-sm text-zinc-600 dark:text-zinc-300 max-h-64 overflow-y-auto pr-1"
          >
            <p>
              NexGridCo stores and processes your color projects, saved palettes and account data
              so we can provide you with a personalized and secure experience across the platform.
            </p>
            <p>
              By continuing, you confirm that you have read and agree with the following documents,
              which describe how we handle your data, your rights, and our responsibilities as a
              service provider:
            </p>
            <ul class="list-disc list-inside space-y-1">
              <li>
                <a
                  href="https://nexgridco.com/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sky-500 hover:text-sky-400 hover:underline"
                >
                  Terms of Use
                </a>
              </li>
              <li>
                <a
                  href="https://nexgridco.com/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sky-500 hover:text-sky-400 hover:underline"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="https://nexgridco.com/legal/dpa"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sky-500 hover:text-sky-400 hover:underline"
                >
                  Data Protection Agreement
                </a>
              </li>
            </ul>
            <p>
              You can access and review these documents at any time from your account settings
              or from the legal section on our website.
            </p>
          </div>

          <!-- Checkboxes -->
          <div class="space-y-3 pt-2">
            <label class="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                v-model="acceptedTerms"
                class="mt-1 h-4 w-4 rounded border-zinc-300 text-sky-500 focus:ring-sky-500"
              />
              <span class="text-sm text-zinc-700 dark:text-zinc-200">
                I have read and agree to the Terms of Use governing my use of NexGridCo.
              </span>
            </label>

            <label class="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                v-model="acceptedPrivacy"
                class="mt-1 h-4 w-4 rounded border-zinc-300 text-sky-500 focus:ring-sky-500"
              />
              <span class="text-sm text-zinc-700 dark:text-zinc-200">
                I agree with the Privacy Policy and Data Protection rules, including how my data may
                be collected, used, and stored.
              </span>
            </label>
          </div>

          <!-- Actions -->
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3">
            <p class="text-xs text-zinc-500 dark:text-zinc-400 max-w-xs">
              You only need to accept these terms once. You can manage your privacy and legal
              preferences at any time from your account settings.
            </p>

            <div class="flex items-center gap-2 justify-end">
              <button
                type="button"
                class="inline-flex items-center justify-center px-3 py-2 rounded-xl text-xs font-medium
                       border border-zinc-200/80 dark:border-zinc-800/80 text-zinc-600 dark:text-zinc-300
                       bg-zinc-100/70 dark:bg-zinc-900/70 hover:bg-zinc-200/70 dark:hover:bg-zinc-800/80"
                @click="logout"
              >
                Log out
              </button>

              <button
                type="button"
                class="inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-medium
                       bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed
                       shadow-sm shadow-sky-500/30"
                :disabled="!canConfirm || loading"
                @click="confirm"
              >
                <span v-if="!loading">Accept and continue</span>
                <span v-else>Saving...</span>
              </button>
            </div>
          </div>

          <p v-if="error" class="text-xs text-red-500 mt-1">
            {{ error }}
          </p>
        </section>
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { API } from '@/router'

const route = useRoute()
const router = useRouter()

const acceptedTerms = ref(false)
const acceptedPrivacy = ref(false)
const loading = ref(false)
const error = ref('')

const canConfirm = computed(() => acceptedTerms.value && acceptedPrivacy.value)

const confirm = async () => {
  if (!canConfirm.value || loading.value) return
  loading.value = true
  error.value = ''

  try {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }

    const res = await fetch(`${API}/billing/accept-legal`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        acceptedTerms: true,
        acceptedPrivacy: true,
      }),
    })

    if (!res.ok) {
      throw new Error('Could not save your consent. Please try again.')
    }

    const next = route.query.next || '/dashboard'
    router.push(next)
  } catch (err) {
    error.value = err.message || 'Error saving your consent.'
  } finally {
    loading.value = false
  }
}

const logout = () => {
  localStorage.removeItem('token')
  sessionStorage.removeItem('token')
  router.push({ name: 'entrar' })
}
</script>
